import { fetchJson, fetchMultipart, fetchDelete } from "@/services/api";
import type {
  ConversationsResponse,
  ConversationResponse,
  ContactsResponse,
  MessagesResponse,
  MessageResponse,
  UsersResponse,
  ApiConversation,
  ApiContact,
  ApiMessage,
  ApiUser,
  CreateConversationPayload,
  SendMessagePayload,
} from "@/types";

export async function getConversations(signal?: AbortSignal): Promise<ApiConversation[]> {
  const res = await fetchJson<ConversationsResponse>("/v1/conversations", { signal });
  return res.data;
}

export async function createConversation(
  payload: CreateConversationPayload,
): Promise<ApiConversation> {
  const res = await fetchJson<ConversationResponse>("/v1/conversations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function getMessages(conversationId: number): Promise<ApiMessage[]> {
  const res = await fetchJson<MessagesResponse>(`/v1/conversations/${conversationId}/messages`);
  // L'API retourne les messages du plus récent au plus ancien — on inverse pour l'affichage
  return res.data.slice().reverse();
}

export async function sendMessage(
  conversationId: number,
  payload: SendMessagePayload,
): Promise<ApiMessage> {
  if (payload.attachment) {
    const form = new FormData();
    form.append("body", payload.body);
    form.append("attachment", payload.attachment);
    const res = await fetchMultipart<MessageResponse>(
      `/v1/conversations/${conversationId}/messages`,
      form,
    );
    return res.data;
  }
  const res = await fetchJson<MessageResponse>(`/v1/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body: payload.body }),
  });
  return res.data;
}

export async function getUnreadCount(): Promise<number> {
  const res = await fetchJson<{ unread_count: number }>("/v1/conversations/unread-count");
  return res.unread_count;
}

export async function markAsRead(conversationId: number): Promise<void> {
  await fetchJson(`/v1/conversations/${conversationId}/mark-read`, { method: "PATCH" });
}

export async function getAllUsers(): Promise<ApiUser[]> {
  const res = await fetchJson<UsersResponse>("/v1/users");
  return res.data;
}

export async function getContacts(): Promise<ApiContact[]> {
  const res = await fetchJson<ContactsResponse>("/v1/conversations/contacts");
  return res.data;
}

export async function broadcastTyping(conversationId: number): Promise<void> {
  await fetchJson(`/v1/conversations/${conversationId}/typing`, { method: "POST" });
}

export async function updateMessage(id: number, body: string): Promise<ApiMessage> {
  const res = await fetchJson<MessageResponse>(`/v1/messages/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ body }),
  });
  return res.data;
}

export async function deleteMessage(id: number): Promise<void> {
  await fetchDelete(`/v1/messages/${id}`);
}
